import { REMEDIATION_CONSTANTS } from '../../../NRRemediation/NRRemediation.constants';
import { isJiraUnassignedAssignee } from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/jiraIntegration.constants';
import {
  IGNORED_JIRA_FIELD_KEYS,
  IGNORED_JIRA_SCHEMA_TYPES,
  JIRA_ASSIGNEE_FIELD_KEY,
  JIRA_PRIORITY_FIELD_KEY,
  JIRA_RPC_OPTION_SOURCE,
  SINGLE_SELECT_SCHEMA_TYPES,
  STATIC_JIRA_FIELD_KEYS,
  ENABLE_JIRA_CREATE_SPRINT_FIELDS
} from './createJiraTicket.constants';

export function normalizeJiraSchemaType(schemaType) {
  return (schemaType || '').toLowerCase().trim();
}

export function hasJiraAllowedValues(field) {
  return Array.isArray(field?.allowedValues) && field.allowedValues.length > 0;
}

export function isIgnoredJiraField(field) {
  if (!field?.key) {
    return true;
  }

  const key = field.key.toLowerCase();
  const schemaType = normalizeJiraSchemaType(field.schemaType);

  return IGNORED_JIRA_FIELD_KEYS.has(key) || IGNORED_JIRA_SCHEMA_TYPES.has(schemaType);
}

export function isStaticJiraField(field) {
  const key = field?.key?.toLowerCase();
  const schemaType = normalizeJiraSchemaType(field?.schemaType);

  return STATIC_JIRA_FIELD_KEYS.has(key) || STATIC_JIRA_FIELD_KEYS.has(schemaType);
}

/**
 * Jira fields for the dynamic form: required RPC-backed fields only
 * (users / versions / components / labels / teams; sprints when enabled).
 * Static-form and ignored fields are always excluded.
 */
export function getDynamicJiraFields(issueFields = []) {
  return (issueFields || []).filter(field => {
    if (isIgnoredJiraField(field) || isStaticJiraField(field)) {
      return false;
    }

    // Only fields with a dedicated option RPC.
    if (!getJiraFieldRpcOptionSource(field)) {
      return false;
    }

    if (!isJiraFieldSupportedForCreate(field)) {
      return false;
    }

    return Boolean(field?.required);
  });
}

/**
 * Whether create-issue can collect this field.
 * Prefer public-api `supported` (static + RPC-backed only); fall back to local RPC checks.
 */
export function isJiraFieldSupportedForCreate(field) {
  if (!field?.key) {
    return false;
  }

  if (typeof field.supported === 'boolean') {
    return field.supported;
  }

  if (isJiraSprintField(field) && !ENABLE_JIRA_CREATE_SPRINT_FIELDS) {
    return false;
  }

  if (isStaticJiraField(field)) {
    return true;
  }

  if (isIgnoredJiraField(field)) {
    return false;
  }

  return Boolean(getJiraFieldRpcOptionSource(field));
}

/**
 * Required space fields create cannot collect.
 * Prefer public-api `unsupportedRequired`; fall back to !supported.
 */
export function getUnsupportedRequiredJiraFields(issueFields = []) {
  return (issueFields || []).filter(field => {
    if (!field?.required || isStaticJiraField(field)) {
      return false;
    }

    if (typeof field.unsupportedRequired === 'boolean') {
      return field.unsupportedRequired;
    }

    return !isJiraFieldSupportedForCreate(field);
  });
}

export function getJiraFieldControlType(field) {
  const schemaType = normalizeJiraSchemaType(field?.schemaType);
  const itemType = normalizeJiraSchemaType(field?.itemType);
  const allowed = hasJiraAllowedValues(field);
  const rpcSource = getJiraFieldRpcOptionSource(field);

  // Sprint / Team are always a single id select — never freeSolo / multiSelect.
  if (
    rpcSource === JIRA_RPC_OPTION_SOURCE.SPRINTS ||
    isJiraSprintField(field) ||
    rpcSource === JIRA_RPC_OPTION_SOURCE.TEAMS ||
    isJiraTeamField(field)
  ) {
    return 'singleSelect';
  }

  // Other id-backed RPC lists: prefer selects over free-text even when itemType is odd.
  if (
    rpcSource === JIRA_RPC_OPTION_SOURCE.VERSIONS ||
    rpcSource === JIRA_RPC_OPTION_SOURCE.COMPONENTS
  ) {
    return schemaType === 'array' ? 'multiSelect' : 'singleSelect';
  }

  switch (schemaType) {
    case 'array':
      if (itemType === 'string') {
        return 'freeTags';
      }
      if (allowed || ['option', 'version', 'component', 'user', 'group'].includes(itemType)) {
        return 'multiSelect';
      }
      return 'freeTags';
    case 'number':
      return allowed ? 'singleSelect' : 'number';
    case 'date':
      return allowed ? 'singleSelect' : 'date';
    case 'datetime':
      return allowed ? 'singleSelect' : 'datetime';
    case 'string':
    case '':
      return allowed ? 'singleSelect' : 'text';
    default:
      if (allowed || SINGLE_SELECT_SCHEMA_TYPES.has(schemaType)) {
        return 'singleSelect';
      }
      return 'text';
  }
}

export function mapJiraAllowedValueOptions(allowedValues = []) {
  return (allowedValues || [])
    .map(option => ({
      value: option.id,
      label: option.value || option.id,
      text: option.value || option.id
    }))
    .filter(option => option.value != null && option.value !== '');
}

/**
 * Field types whose options can be loaded from dedicated RPCs
 * (versions / components / labels / users / sprints / teams).
 */
export function isJiraSprintField(field) {
  if (!field?.key && !field?.name) {
    return false;
  }

  const schemaType = normalizeJiraSchemaType(field?.schemaType);
  const itemType = normalizeJiraSchemaType(field?.itemType);
  const key = (field?.key || '').toLowerCase();
  const name = (field?.name || '').toLowerCase();

  return (
    key === 'sprint' ||
    key.includes('sprint') ||
    name === 'sprint' ||
    schemaType === 'sprint' ||
    itemType === 'sprint'
  );
}

export function isJiraTeamField(field) {
  if (!field?.key && !field?.name) {
    return false;
  }

  const schemaType = normalizeJiraSchemaType(field?.schemaType);
  const itemType = normalizeJiraSchemaType(field?.itemType);
  const key = (field?.key || '').toLowerCase();
  const name = (field?.name || '').toLowerCase();

  return key === 'team' || name === 'team' || schemaType === 'team' || itemType === 'team';
}

export function getJiraFieldRpcOptionSource(field) {
  if (!field?.key) {
    return null;
  }

  const schemaType = normalizeJiraSchemaType(field.schemaType);
  const itemType = normalizeJiraSchemaType(field.itemType);
  const key = field.key.toLowerCase();

  if (
    schemaType === 'version' ||
    itemType === 'version' ||
    key === 'fixversions' ||
    key === 'versions' ||
    key === 'version'
  ) {
    return JIRA_RPC_OPTION_SOURCE.VERSIONS;
  }

  if (schemaType === 'component' || itemType === 'component' || key === 'components' || key === 'component') {
    return JIRA_RPC_OPTION_SOURCE.COMPONENTS;
  }

  if (schemaType === 'user' || itemType === 'user') {
    return JIRA_RPC_OPTION_SOURCE.USERS;
  }

  // System Labels (and label-like custom fields): array of strings, options from SearchJiraLabels.
  if (key === 'labels' || (schemaType === 'array' && itemType === 'string' && key.includes('label'))) {
    return JIRA_RPC_OPTION_SOURCE.LABELS;
  }

  if (isJiraSprintField(field)) {
    return ENABLE_JIRA_CREATE_SPRINT_FIELDS ? JIRA_RPC_OPTION_SOURCE.SPRINTS : null;
  }

  if (isJiraTeamField(field)) {
    return JIRA_RPC_OPTION_SOURCE.TEAMS;
  }

  return null;
}

export function getRequiredRpcOptionSources(fields = []) {
  const sources = new Set();

  (fields || []).forEach(field => {
    const source = getJiraFieldRpcOptionSource(field);
    if (source) {
      sources.add(source);
    }
  });

  return sources;
}

function mapIdNameOptions(items = []) {
  return (items || [])
    .map(item => ({
      value: item.id,
      label: item.name || item.id,
      text: item.name || item.id
    }))
    .filter(option => option.value != null && option.value !== '');
}

export function mapRpcOptionsToFieldOptions(source, rpcOptions = {}) {
  switch (source) {
    case JIRA_RPC_OPTION_SOURCE.VERSIONS:
      return mapIdNameOptions(rpcOptions.versions);
    case JIRA_RPC_OPTION_SOURCE.COMPONENTS:
      return mapIdNameOptions(rpcOptions.components);
    case JIRA_RPC_OPTION_SOURCE.USERS:
      return (rpcOptions.users || [])
        .map(user => ({
          value: user.accountId,
          label: user.displayName || user.accountId,
          text: user.displayName || user.accountId
        }))
        .filter(option => option.value);
    case JIRA_RPC_OPTION_SOURCE.LABELS:
      return (rpcOptions.labels || [])
        .map(label => {
          const name = label?.name;
          return name
            ? {
                value: name,
                label: name,
                text: name
              }
            : null;
        })
        .filter(Boolean);
    case JIRA_RPC_OPTION_SOURCE.SPRINTS:
      return mapIdNameOptions(rpcOptions.sprints);
    case JIRA_RPC_OPTION_SOURCE.TEAMS:
      return (rpcOptions.teams || [])
        .map(team => ({
          value: team.id,
          label: team.displayName || team.id,
          text: team.displayName || team.id,
          avatarUrl: team.avatarUrl
        }))
        .filter(option => option.value);
    default:
      return [];
  }
}

/** Prefer RPC-backed option lists; fall back to field.allowedValues. */
export function resolveJiraFieldOptions(field, rpcOptions = {}) {
  const source = getJiraFieldRpcOptionSource(field);
  if (source) {
    const rpcFieldOptions = mapRpcOptionsToFieldOptions(source, rpcOptions);
    if (rpcFieldOptions.length > 0) {
      return rpcFieldOptions;
    }
  }

  if (hasJiraAllowedValues(field)) {
    return mapJiraAllowedValueOptions(field.allowedValues);
  }

  return [];
}

export function getJiraFieldDefaultValue(field) {
  const controlType = getJiraFieldControlType(field);

  if (controlType === 'multiSelect' || controlType === 'freeTags') {
    if (field?.required && field?.allowedValues?.length === 1) {
      return [field.allowedValues[0].id];
    }
    return [];
  }

  if (
    field?.required &&
    field?.allowedValues?.length === 1 &&
    (controlType === 'singleSelect' || hasJiraAllowedValues(field))
  ) {
    return field.allowedValues[0].id;
  }

  return '';
}

export function buildJiraDynamicDefaultValues(fields = []) {
  return (fields || []).reduce((defaults, field) => {
    if (!field?.key) {
      return defaults;
    }
    defaults[field.key] = getJiraFieldDefaultValue(field);
    return defaults;
  }, {});
}

function isEmptyJiraFormValue(value) {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
}

/**
 * Transforms react-hook-form values into JiraFieldValueInput[] for createIssue.
 * Option-backed fields send selected ids; free-text / free-tag fields send string values.
 */
export function formatJiraPayload(formData = {}, schema = []) {
  return (schema || [])
    .map(field => {
      if (!field?.key) {
        return null;
      }

      const rawValue = formData[field.key];
      if (isEmptyJiraFormValue(rawValue)) {
        return null;
      }

      const controlType = getJiraFieldControlType(field);
      let values = [];

      if (controlType === 'multiSelect' || controlType === 'freeTags') {
        values = (Array.isArray(rawValue) ? rawValue : [rawValue])
          .map(value => String(value).trim())
          .filter(Boolean);
      } else {
        values = [String(rawValue).trim()].filter(Boolean);
      }

      if (!values.length) {
        return null;
      }

      return {
        key: field.key,
        values,
        schemaType: field.schemaType || 'string',
        itemType: field.itemType || ''
      };
    })
    .filter(Boolean);
}

export function getJiraFieldByKey(fields = [], key) {
  const normalizedKey = key?.toLowerCase();

  return (
    fields.find(field => field.key?.toLowerCase() === normalizedKey) ||
    fields.find(field => field.schemaType?.toLowerCase() === normalizedKey) ||
    fields.find(field => field.name?.toLowerCase() === normalizedKey) ||
    null
  );
}

/**
 * Priority options from the space/issue-type schema (id for submit, label for display).
 */
export function getPriorityOptionsFromFields(issueFields = []) {
  const priorityField = getJiraFieldByKey(issueFields, JIRA_PRIORITY_FIELD_KEY);

  return (priorityField?.allowedValues ?? [])
    .map(value => ({
      id: value.id,
      label: value.value || value.id
    }))
    .filter(option => option.id);
}

/**
 * TODO: After verifying per-space schemas, optionally require `priorityField.required`
 * instead of showing whenever the field exists with allowedValues.
 */
export function shouldShowPriorityField(issueFields = []) {
  const priorityField = getJiraFieldByKey(issueFields, JIRA_PRIORITY_FIELD_KEY);
  return Boolean(priorityField && hasJiraAllowedValues(priorityField));
}

export function buildCreateJiraIssueFields({ assigneeId, priorityId, issueFields = [] } = {}) {
  const fields = [];
  const assigneeField = getJiraFieldByKey(issueFields, JIRA_ASSIGNEE_FIELD_KEY);
  const priorityField = getJiraFieldByKey(issueFields, JIRA_PRIORITY_FIELD_KEY);

  if (priorityId) {
    fields.push({
      key: priorityField?.key || JIRA_PRIORITY_FIELD_KEY,
      values: [priorityId],
      schemaType: priorityField?.schemaType || 'priority',
      itemType: priorityField?.itemType || ''
    });
  }

  // Unassigned = omit assignee. Protos have no Unassigned user; Jira treats missing assignee as unassigned.
  if (!isJiraUnassignedAssignee(assigneeId)) {
    fields.push({
      key: assigneeField?.key || JIRA_ASSIGNEE_FIELD_KEY,
      values: [assigneeId],
      schemaType: assigneeField?.schemaType || 'user',
      itemType: assigneeField?.itemType || ''
    });
  }

  return fields;
}

export function buildCreateJiraTicketSummary({ cve, name, version, severity } = {}) {
  const parts = [cve, name && version ? `${name} ${version}` : name, severity].filter(Boolean);
  return parts.join(' — ');
}

/**
 * Create is blocked until the issue-type schema fetch has settled for the current selection.
 * Avoids submitting without required custom fields while jiraSpaceIssueFields is still loading.
 */
export function isJiraIssueFieldsReady({ hasIssueType, issueFieldsCalled, issueFieldsLoading } = {}) {
  return Boolean(hasIssueType && issueFieldsCalled && !issueFieldsLoading);
}

function formatEpssScore(epssScore) {
  if (epssScore == null || isNaN(Number(epssScore))) {
    return null;
  }

  return `${(Number(epssScore) * 100).toFixed(1)}%`;
}

function formatFixVersions(fixVersions) {
  if (!fixVersions?.length) {
    return null;
  }

  return Array.isArray(fixVersions) ? fixVersions.join(', ') : String(fixVersions);
}

function formatVexStatus(status) {
  if (!status) {
    return null;
  }

  return REMEDIATION_CONSTANTS.STATUS.LABELS[status] || status;
}

function formatReachability(isReachable) {
  if (isReachable == null) {
    return null;
  }

  return isReachable ? 'Reachable' : 'Not reachable';
}

function formatComponent({ name, version } = {}) {
  if (!name) {
    return null;
  }

  return version ? `${name} ${version}` : name;
}

export function buildCreateJiraTicketDescription({
  severity,
  cvssScore,
  epssScore,
  fixVersions,
  vexStatus,
  isReachable,
  name,
  version,
  assetName,
  assetId
} = {}) {
  const lines = [];

  const severityValue = [severity, cvssScore != null && !isNaN(Number(cvssScore)) ? `(${Number(cvssScore).toFixed(1)})` : null]
    .filter(Boolean)
    .join(' ');
  if (severityValue) {
    lines.push(`Severity: ${severityValue}`);
  }

  const epssValue = formatEpssScore(epssScore);
  if (epssValue) {
    lines.push(`EPSS Score: ${epssValue}`);
  }

  const fixVersionValue = formatFixVersions(fixVersions);
  if (fixVersionValue) {
    lines.push(`Fix Version: ${fixVersionValue}`);
  }

  const vexStatusValue = formatVexStatus(vexStatus);
  if (vexStatusValue) {
    lines.push(`VEX Status: ${vexStatusValue}`);
  }

  const reachabilityValue = formatReachability(isReachable);
  if (reachabilityValue) {
    lines.push(`Reachability: ${reachabilityValue}`);
  }

  const componentValue = formatComponent({ name, version });
  if (componentValue) {
    lines.push(`Component: ${componentValue}`);
  }

  if (assetName) {
    lines.push(`Asset: ${assetName}`);
  }

  if (assetId) {
    lines.push(`Asset ID: ${assetId}`);
  }

  return lines.join('\n');
}
